<template>
  <div class="location-highlights-page">
    <div v-if="isLoading" style="margin-bottom: 35px; display: flex; flex-direction: column; align-items: center; text-align: center">
      <LoaderNew style="width: 70px; height: 70px; margin-bottom: 10px" :borderWidth="3" />
    </div>

    <div v-else-if="noCalendarsFound" class="no-calendars-message" style="margin-bottom: 35px; display: flex; flex-direction: column; align-items: center; text-align: center">
      <h2 class="global-h2">No Calendars Found</h2>
      <p class="global-p">Please contact Shrpa to get started.</p>
    </div>

    <template v-else>
      <!-- Header -->
      <div class="location-highlights-page__header">
        <div class="location-highlights-page__header-copy">
          <CalendarPicker :customerId="customerId" suffix="Local Promotion" hideSocialPrefix />
          <p class="global-p location-highlights-page__subtitle">Show how your social content supports local businesses and events.</p>
          <!-- <div v-if="isCalendarLineVisible" class="location-highlights-page__calendar-line">Calendar: {{ calendarDisplayName }}</div> -->
        </div>

        <div class="location-highlights-page__filters">
          <LoadingIcon v-if="isRefreshing" :size="16" />
          <ReportFilterDropdown :options="POST_FILTER_OPTIONS" :modelValue="postFilter" @update:modelValue="value => (postFilter = value as LocationHighlightsPostFilter)" />
          <ReportFilterDropdown label="Dates" :options="DATE_RANGE_OPTIONS" :modelValue="dateRange" @update:modelValue="value => (dateRange = value as LocationHighlightsDateRange)" />
        </div>
      </div>

      <!-- Load error -->
      <NoteWithIcon v-if="loadError" class="location-highlights-page__note" color="red" size="small">
        <template #text>
          <div>{{ loadError }}</div>
          <LinkWithIcon class="location-highlights-page__retry" isDottedUnderline color="red" @click="loadReport(true)">Try again</LinkWithIcon>
        </template>
      </NoteWithIcon>

      <!-- Report body -->
      <template v-if="report">
        <!-- No locations saved yet -->
        <NoteWithIcon v-if="report.locationCount === 0 && !report.peopleRows?.length && !report.archivedRows?.length" class="location-highlights-page__note" color="gray" size="small">
          <template #text>
            <div>No locations saved yet</div>
            <div class="location-highlights-page__empty-actions">
              <SrpButton v-if="!isLocationReviewCalloutVisible" color="teal" size="small" :to="{ name: 'AtlasOnboardingLocations', params: { customerId } }">Review Suggestions</SrpButton>
              <SrpButton tag="button" type="button" color="gray" fill="outlined" size="small" @click="openLocationEditor(null)">Add manually</SrpButton>
            </div>
          </template>
        </NoteWithIcon>

        <template v-if="report.locationCount > 0 || report.peopleRows?.length || report.archivedRows?.length">
          <NoteWithIcon v-if="report.postCount === 0" class="location-highlights-page__note" color="gray" size="small">
            <template #text><div>No posts match these filters</div></template>
          </NoteWithIcon>

          <div v-if="report.locationCount > 0" class="location-highlights-page__cards-row">
            <MostHighlightedLocations :rows="topHighlightedRows" />
            <NotYetHighlightedLocations :rows="notYetRows" @selectLocation="handleSelectLocation" />
            <LocationCoverageBar
              :highlightedCount="report.locationsHighlightedCount"
              :totalCount="report.locationCount"
              :postCount="report.postsWithLocationsCount"
              :rows="report.rows"
              :locationsById="locationsById"
            />
          </div>
        </template>

        <LocationReviewCallout :customerId="customerId" source="localPromotion" @visibilityChange="isLocationReviewCalloutVisible = $event" />

        <LocationHighlightsTable
          v-if="report.locationCount > 0 || report.peopleRows?.length || report.archivedRows?.length"
          ref="tableRef"
          :customerId="customerId"
          :rows="report.rows"
          :peopleRows="report.peopleRows ?? []"
          :archivedRows="report.archivedRows ?? []"
          :locationsById="locationsById"
          :activeTab="activeTab"
          @update:activeTab="value => (activeTab = value)"
          @addLocation="openLocationEditor(null)"
          @editLocation="openLocationEditor"
          @newPost="startNewPost"
          @viewPosts="openLocationPosts"
          @photosSaved="handlePhotosSaved"
        />
      </template>
    </template>

    <LocationEditorModal v-model:isVisible="isLocationEditorVisible" :customerId="customerId" :location="locationToEdit" @saved="handleLocationSaved" @afterClose="handleLocationEditorClosed" />
    <LocationPostsModal
      v-if="locationPostsContext"
      v-model:isVisible="isLocationPostsVisible"
      :customerId="customerId"
      :calendarId="locationPostsContext.calendarId"
      :locationId="locationPostsContext.locationId"
      :postFilter="locationPostsContext.postFilter"
      :startDate="locationPostsContext.startDate"
      :endDate="locationPostsContext.endDate"
      :filterLabel="locationPostsContext.filterLabel"
    />

    <div style="margin-top: 50px; display: flex; justify-content: flex-end; width: 100%">
      <SuperUserActionPanel>
        <div style="display: flex; gap: 10px">
          <SrpButton color="gray" fill="outlined" size="tiny" :to="{ name: 'AtlasOnboardingLocations', params: { customerId } }">Location Seeding</SrpButton>
          <SrpButton color="gray" fill="outlined" size="tiny" :to="{ name: 'LocationSocialMetricsReport', params: { customerId } }">Social Analytics</SrpButton>
        </div>
      </SuperUserActionPanel>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useHead } from "@unhead/vue";
import { MetricSender } from "@/helpers/MetricSender";
import { getGlobalRemoteLogger } from "@/helpers/RemoteLogger";
import AdminContext from "@logic/AdminContext";
import DayjsExtended from "@logic/DayjsExtended";
import { getShowTestCalendars } from "@logic/calendarManager";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import SocialLocationsRepo from "@repos/SocialLocationsRepo";
import SocialMediaSettingsRepo from "@repos/SocialMediaSettingsRepo";
import SocialReportsRepo from "@repos/SocialReportsRepo";

// Components
import LocationReviewCallout from "@components/LocationReviewCallout.vue";
import CalendarPicker from "@components/CalendarPicker.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import LoaderNew from "@components/Loader/LoaderNew.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import LocationEditorModal from "@components/SocialLocations/LocationEditorModal.vue";
import LocationPostsModal from "@components/SocialLocations/LocationPostsModal.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import SuperUserActionPanel from "@components/SuperUserActionPanel.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import LocationCoverageBar from "./LocationHighlights/LocationCoverageBar.vue";
import LocationHighlightsTable from "./LocationHighlights/LocationHighlightsTable.vue";
import MostHighlightedLocations from "./LocationHighlights/MostHighlightedLocations.vue";
import NotYetHighlightedLocations from "./LocationHighlights/NotYetHighlightedLocations.vue";
import ReportFilterDropdown from "./shared/ReportFilterDropdown.vue";

// Types
import type { AtlasOnboardingStep } from "@contracts/socialMediaSettings";
import type { LocationData } from "@contracts/socialLocations";
import type { LocationHighlightRow, LocationHighlightsDateRange, LocationHighlightsPostFilter, LocationHighlightsReport } from "@contracts/socialReports";

defineOptions({
  name: "LocationHighlightsReport",
});

// Router
const route = useRoute();
const router = useRouter();

// Normalize route params ------------------------------------------------------
const customerId = computed<string>(() => (Array.isArray(route.params.customerId) ? route.params.customerId[0] : (route.params.customerId as string)));

const pageTitle = ref("Local Promotion");
useHead({ title: () => pageTitle.value });

// Filter options =============================================================
const POST_FILTER_OPTIONS = [
  { value: "All", label: "All Posts (including drafts)" },
  { value: "OnlyApproved", label: "Only Approved" },
  { value: "OnlyPublished", label: "Only Published" },
];

const DATE_RANGE_OPTIONS = [
  { value: "ever", label: "Ever" },
  { value: "lastYear", label: "Last Year" },
  { value: "lastQuarter", label: "Last Quarter" },
  { value: "lastMonth", label: "Last Month" },
];

// State ======================================================================
const report = ref<LocationHighlightsReport | null>(null);
const postFilter = ref<LocationHighlightsPostFilter>("All");
const dateRange = ref<LocationHighlightsDateRange>("ever");
const reportDateRange = ref<LocationHighlightsDateRange>("ever");
const activeTab = ref<"highlighted" | "notYet" | "all">("all");
const isLoading = ref<boolean>(true);
const isRefreshing = ref<boolean>(false);
const loadError = ref<string>("");

const noCalendarsFound = ref<boolean>(false);
const isLocationReviewCalloutVisible = ref(false);

const tableRef = ref<InstanceType<typeof LocationHighlightsTable> | null>(null);

// Saved locations ==========================================================
// The report rows only carry what the aggregation needs; socials, address, category and the
// object the editor works on come from the saved locations themselves.
const locations = ref<LocationData[]>([]);
const locationsById = computed<Record<string, LocationData>>(() => Object.fromEntries(locations.value.map(location => [location.id, location])));

async function loadLocations() {
  try {
    locations.value = await SocialLocationsRepo.list(customerId.value, true, false);
  } catch (error) {
    // Non-blocking: the table still lists every row, just without handles, type icons and edit.
    getGlobalRemoteLogger()?.error(`LocationHighlightsPage.loadLocations failed. customerId=${customerId.value} error=${error}`);
  }
}

// Add / edit a location ======================================================
const isLocationEditorVisible = ref<boolean>(false);
const locationToEdit = ref<LocationData | null>(null);
let locationEditorSession = 0;
let isLocationEditorClosed = true;
let pendingAddedLocation: LocationData | null = null;

function openLocationEditor(location: LocationData | null) {
  locationEditorSession++;
  pendingAddedLocation = null;
  isLocationEditorClosed = false;
  locationToEdit.value = location;
  isLocationEditorVisible.value = true;
}

async function handleLocationSaved(savedLocation: LocationData) {
  const isNewLocation = !locationToEdit.value;
  const session = locationEditorSession;
  const [, reportLoaded] = await Promise.all([loadLocations(), loadReport(true)]);
  if (!isNewLocation || !reportLoaded || session !== locationEditorSession) return;

  // The first saved account mounts the table; wait for its ref before revealing the row.
  await nextTick();
  if (session !== locationEditorSession || !tableRef.value) return;
  const rows = [...(report.value?.rows ?? []), ...(report.value?.peopleRows ?? [])];
  if (!rows.some(row => row.locationId === savedLocation.id)) return;

  pendingAddedLocation = savedLocation;
  await scrollToAddedLocation();
}

async function handleLocationEditorClosed() {
  isLocationEditorClosed = true;
  await scrollToAddedLocation();
}

async function scrollToAddedLocation() {
  const location = pendingAddedLocation;
  const table = tableRef.value;
  if (!location || !table || !isLocationEditorClosed) return;

  // Refresh and modal closure can finish in either order. Consume the target only once both finish.
  pendingAddedLocation = null;
  if (location.tagKind === "Person") table.showAddedPerson();
  await table.scrollToLocation(location.id);
}

// New post for a location ====================================================
function startNewPost(locationId: string) {
  MetricSender.sendMetric("Cust-Report-LocationHighlights-NewPost", { customerId: customerId.value, locationId });
  router.push({ name: "CalendarPosts", params: { customerId: customerId.value }, query: { newPostLocationId: locationId } });
}

// Snapshot the displayed report's filters so the modal agrees with the clicked count, even during a refresh.
const isLocationPostsVisible = ref(false);
const locationPostsContext = ref<{
  calendarId: string;
  locationId: string;
  postFilter: LocationHighlightsPostFilter;
  startDate?: string | null;
  endDate?: string | null;
  filterLabel: string;
} | null>(null);

function openLocationPosts(locationId: string) {
  if (!report.value) return;
  const { calendarId, postFilter: displayedPostFilter, startDate, endDate } = report.value;
  const statusLabel = POST_FILTER_OPTIONS.find(option => option.value === displayedPostFilter)?.label ?? displayedPostFilter;
  const dateLabel = DATE_RANGE_OPTIONS.find(option => option.value === reportDateRange.value)?.label ?? "Ever";
  locationPostsContext.value = { calendarId, locationId, postFilter: displayedPostFilter, startDate, endDate, filterLabel: `${statusLabel} · ${dateLabel}` };
  isLocationPostsVisible.value = true;
}

// Photos uploaded from the list ==============================================
const MAX_PREVIEW_PHOTOS = 5; // Same as the server's LocationHighlightsReportBuilder.MaxPreviewPhotos.

/** A batch saved to a Location's album (newest first): update its row in place rather than reloading the report. */
function handlePhotosSaved(locationId: string, assetIds: Array<string>) {
  const row = [...(report.value?.rows ?? []), ...(report.value?.archivedRows ?? [])].find(r => r.locationId === locationId);
  if (!row) return;
  const added = new Set(assetIds);
  row.photoCount += assetIds.length;
  row.previewPhotoIds = [...assetIds, ...(row.previewPhotoIds ?? []).filter(photoId => !added.has(photoId))].slice(0, MAX_PREVIEW_PHOTOS);
}

// Derived rows ===============================================================
// Ranked by posts (not the backend's post x channel order), newest highlight first on ties.
const topHighlightedRows = computed<LocationHighlightRow[]>(() =>
  (report.value?.rows ?? [])
    .filter(r => r.postCount > 0)
    .sort((a, b) => b.postCount - a.postCount || (b.lastHighlightedAt ?? "").localeCompare(a.lastHighlightedAt ?? ""))
    .slice(0, 4)
);

// Show locations ready to promote first, then A-Z within each photo group.
const notYetRows = computed<LocationHighlightRow[]>(() =>
  (report.value?.rows ?? [])
    .filter(r => r.postCount === 0)
    .sort((a, b) => Number(b.photoCount > 0) - Number(a.photoCount > 0) || (a.name ?? "").localeCompare(b.name ?? ""))
);

// Date range -> params =======================================================
function getDateParams(): { startDate?: string; endDate?: string } {
  if (dateRange.value === "ever") return {};

  const today = DayjsExtended();
  const endDate = today.format("YYYY-MM-DD");

  if (dateRange.value === "lastYear") return { startDate: today.subtract(1, "year").format("YYYY-MM-DD"), endDate };
  if (dateRange.value === "lastQuarter") return { startDate: today.subtract(3, "month").format("YYYY-MM-DD"), endDate };
  return { startDate: today.subtract(1, "month").format("YYYY-MM-DD"), endDate };
}

// Load =======================================================================
let reportLoadSequence = 0;
async function loadReport(isRefresh: boolean): Promise<boolean> {
  const sequence = ++reportLoadSequence;
  const requestedDateRange = dateRange.value;
  if (isRefresh) isRefreshing.value = true;
  loadError.value = "";

  const { startDate, endDate } = getDateParams();

  try {
    const result = await SocialReportsRepo.getLocationHighlights(customerId.value, postFilter.value, startDate, endDate);
    if (sequence !== reportLoadSequence) return false;
    report.value = result;
    reportDateRange.value = requestedDateRange;
    return true;
  } catch (error) {
    if (sequence !== reportLoadSequence) return false;
    getGlobalRemoteLogger()?.error(`LocationHighlightsPage.loadReport failed. customerId=${customerId.value} postFilter=${postFilter.value} error=${error}`);
    loadError.value = "We couldn't load this report.";
    return false;
  } finally {
    if (sequence === reportLoadSequence) {
      isRefreshing.value = false;
      isLoading.value = false;
    }
  }
}

watch([postFilter, dateRange], () => loadReport(true));

// Jump from the Not yet highlighted card to the matching table row ============
async function handleSelectLocation(locationId: string) {
  await tableRef.value?.scrollToLocation(locationId);
}

// Onboarding ================================================================
// This page is the "Local Promotion" nav entry, so it decides whether the customer belongs in onboarding instead.
// Returns true when it navigated away.
async function redirectForOnboarding(): Promise<boolean> {
  let onboarding: AtlasOnboardingStep | null | undefined;
  try {
    onboarding = (await SocialMediaSettingsRepo.getSettings(customerId.value)).settings?.onboarding;
  } catch (error) {
    // Non-blocking: without the settings we just show the report.
    getGlobalRemoteLogger()?.error(`LocationHighlightsPage.redirectForOnboarding failed. customerId=${customerId.value} error=${error}`);
    return false;
  }

  // replace, so Back doesn't land here and bounce forward again.
  // NeedsLocations goes to the locations step too: it shows discovery while it runs, and "add your own" when it found nothing.
  if (onboarding === "NeedsLocations" || onboarding === "ReviewLocations") {
    await router.replace({ name: "AtlasOnboardingLocations", params: { customerId: customerId.value } });
    return true;
  }
  if (onboarding === "UploadPhotos") {
    await router.replace({ name: "CalendarUploadContent", params: { customerId: customerId.value } });
    return true;
  }
  // Done, or never started: show the report.
  return false;
}

onMounted(async () => {
  const org = await AdminContext.setOrgInContextFromCustomerId(customerId.value);
  if (org?.name) pageTitle.value = `Local Promotion - ${org.name}`;

  const calendars = await ContentCalendarRepo.getCalendarsForCustomer(customerId.value, getShowTestCalendars());
  noCalendarsFound.value = calendars.length === 0;

  if (noCalendarsFound.value) {
    isLoading.value = false;
    return;
  }

  if (await redirectForOnboarding()) return;

  MetricSender.sendMetric("Cust-Report-LocationHighlights-PageView", { customerId: customerId.value });

  await Promise.all([loadLocations(), loadReport(false)]);
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Location highlights page ===================================================
.location-highlights-page {
  width: $desktop-wide-right-col-width;
  margin: 0 auto;

  &__header {
    margin-bottom: 22px;
    gap: 20px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    flex-wrap: wrap;
  }

  &__header-copy {
    min-width: 0;
    flex-grow: 1;
  }

  &__subtitle {
    margin: 7px 0 0;
  }

  &__calendar-line {
    margin-top: 6px;
    color: #667085;
    font: 400 14px/18px sans-serif;
  }

  &__filters {
    gap: 18px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding-bottom: 4px;
  }

  &__note {
    margin-bottom: 22px;
  }

  &__empty-actions {
    margin-top: 12px;
    gap: 10px;
    display: flex;
    flex-wrap: wrap;
  }

  &__retry {
    margin-top: 6px;
  }

  &__cards-row {
    margin-bottom: 30px;
    gap: 20px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.4fr);
    align-items: stretch;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .location-highlights-page {
    width: $desktop-right-col-width;
  }
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .location-highlights-page {
    width: $laptop-right-col-width;
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .location-highlights-page {
    width: $tablet-large-content-width;
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .location-highlights-page {
    width: $tablet-content-width;

    &__cards-row {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .location-highlights-page {
    width: $mobile-content-width;

    &__cards-row {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}
</style>
